import React from 'react';
import PropTypes from 'prop-types';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { DataGrid } from '../../../DataGrid';
import { batches as batchesApi } from '@afosto/content/api/Batches';
import { batchFormatter, datasetFormatter, typeFormatter, statusFormatter } from './formatters';

const fetchBatches = actionsCode => async ({ pagination = {} }) => {
  try {
    const { activePage, itemsPerPage, getPageCursor, setNextPageCursor, setItemCount } = pagination;
    const options = {
      headers: {
        'x-page-size': itemsPerPage,
      },
    };

    if (activePage || activePage === 0) {
      const pageCursor = getPageCursor(activePage);

      if (pageCursor) {
        options.headers = {
          ...options.headers,
          'x-page': pageCursor,
        };
      } else {
        options.headers = {
          ...options.headers,
          'x-page': activePage + 1,
        };
      }
    }

    const response = await batchesApi.getBatches(actionsCode ? { code: actionsCode } : {}, options);
    const { data = [] } = response;

    const itemCount = response.headers['x-count'];
    const pageCursor = response.headers['x-page'];

    if (pageCursor) {
      setNextPageCursor(pageCursor);
    }

    if (itemCount) {
      setItemCount(itemCount);
    }

    return data;
  } catch (err) {
    return [];
  }
};

const BatchesGrid = ({ actionsCode }) => {
  const intl = useIntl();
  const dataSource = useDataGrid(fetchBatches(actionsCode), {
    modules: ['dataProperties', 'pagination'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'reference',
          name: intl.formatMessage({
            id: 'dataGrid.columns.batches.reference',
            defaultMessage: 'Reference',
            description: 'The column name for column reference',
          }),
          type: 'batch',
        },
        {
          key: 'action_id',
          name: intl.formatMessage({
            id: 'dataGrid.columns.batches.action',
            defaultMessage: 'Action',
            description: 'The column name for column action',
          }),
          type: 'translation',
          formatOptions: {
            translationKey: 'batchActions.{value}',
          },
        },
        {
          key: 'dataset_id',
          name: intl.formatMessage({
            id: 'dataGrid.columns.batches.dataset',
            defaultMessage: 'Dataset',
            description: 'The column name for column dataset id',
          }),
          type: 'dataset',
        },
        {
          key: 'is_import',
          name: intl.formatMessage({
            id: 'dataGrid.columns.batches.type',
            defaultMessage: 'Type',
            description: 'The column name for column is import',
          }),
          type: 'type',
        },
        {
          key: 'status',
          name: intl.formatMessage({
            id: 'dataGrid.columns.batches.status',
            defaultMessage: 'Status',
            description: 'The column name for column status',
          }),
          type: 'status',
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.batches.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'datetime',
        },
      ],
    },
  });

  return (
    <DataGrid
      dataSource={dataSource}
      formatters={{
        batch: batchFormatter,
        dataset: datasetFormatter,
        status: statusFormatter,
        type: typeFormatter,
      }}
    />
  );
};

BatchesGrid.propTypes = {
  actionsCode: PropTypes.string,
};

BatchesGrid.defaultProps = {
  actionsCode: undefined,
};

export default BatchesGrid;
